import Decimal from 'decimal.js';

/**
 * All financial arithmetic goes through this Decimal clone. Never use JS
 * numbers for money: 0.1 + 0.2 !== 0.3.
 *
 * Rounding policy (docs/financial-calculations.md):
 *   - Derived money (GST amount, INR conversion) is rounded to 2 dp (paise), half-up.
 *   - Stored precision is DECIMAL(18,4); ratios are not rounded until display.
 */
export const Money = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export type MoneyValue = InstanceType<typeof Money>;
export type DecimalInput = string | number | MoneyValue | { toString(): string };

export const MONEY_DP = 2;
export const STORAGE_DP = 4;

export function toDecimal(value: DecimalInput): MoneyValue {
  if (value instanceof Money) return value;
  return new Money(typeof value === 'number' ? value : value.toString());
}

export function round2(value: DecimalInput): MoneyValue {
  return toDecimal(value).toDecimalPlaces(MONEY_DP, Money.ROUND_HALF_UP);
}

export interface GstBreakdown {
  beforeGst: MoneyValue;
  gstRate: MoneyValue;
  gstAmount: MoneyValue;
  totalAmount: MoneyValue;
}

/**
 * GST Amount = Expense Before GST × GST Rate / 100 (rounded to paise)
 * Total      = Expense Before GST + GST Amount
 */
export function calculateGst(beforeGst: DecimalInput, gstRatePercent: DecimalInput): GstBreakdown {
  const base = toDecimal(beforeGst);
  const rate = toDecimal(gstRatePercent);
  if (base.isNegative()) throw new RangeError('Amount before GST cannot be negative');
  if (rate.isNegative() || rate.greaterThan(100)) throw new RangeError('GST rate must be 0–100');
  const gstAmount = round2(base.times(rate).dividedBy(100));
  return { beforeGst: base, gstRate: rate, gstAmount, totalAmount: base.plus(gstAmount) };
}

/** INR = amount × exchange rate (rounded to paise). INR amounts use rate 1. */
export function convertToInr(amount: DecimalInput, exchangeRateToInr: DecimalInput): MoneyValue {
  const rate = toDecimal(exchangeRateToInr);
  if (rate.lessThanOrEqualTo(0)) throw new RangeError('Exchange rate must be greater than zero');
  return round2(toDecimal(amount).times(rate));
}

/** Division that returns null instead of Infinity/NaN when the divisor is zero. */
export function safeDivide(numerator: DecimalInput, denominator: DecimalInput): MoneyValue | null {
  const d = toDecimal(denominator);
  if (d.isZero()) return null;
  return toDecimal(numerator).dividedBy(d);
}

/** Serialize for the API / DB: fixed decimal string, never a JS number. */
export function toFixedString(value: DecimalInput, dp = STORAGE_DP): string {
  return toDecimal(value).toFixed(dp);
}

export function sumDecimals(values: DecimalInput[]): MoneyValue {
  return values.reduce<MoneyValue>((acc, v) => acc.plus(toDecimal(v)), new Money(0));
}

/**
 * Split `amount` (rounded to paise) across `weights` in proportion, so the
 * parts always add up exactly. Rounding leftovers go to the largest weight.
 * All-zero (or no positive) weights split equally. Used to share a month-end
 * adjustment between the apps that used an account.
 */
export function splitProRata(amount: DecimalInput, weights: DecimalInput[]): MoneyValue[] {
  if (weights.length === 0) return [];
  const total = round2(amount);
  const w = weights.map((x) => Money.max(toDecimal(x), 0));
  const sum = sumDecimals(w);
  const basis = sum.isZero() ? w.map(() => new Money(1)) : w;
  const basisSum = sumDecimals(basis);
  const parts = basis.map((b) => round2(total.times(b).dividedBy(basisSum)));
  const leftover = total.minus(sumDecimals(parts));
  if (!leftover.isZero()) {
    let largest = 0;
    basis.forEach((b, i) => {
      if (b.greaterThan(basis[largest]!)) largest = i;
    });
    parts[largest] = parts[largest]!.plus(leftover);
  }
  return parts;
}

/**
 * Standard AdMob / AdX payment thresholds by payout currency. Earnings below
 * the threshold are carried to the next month (docs/business-rules.md M-section).
 */
export const PAYOUT_THRESHOLD_DEFAULTS: Readonly<Record<string, string>> = {
  USD: '100',
  EUR: '70',
  GBP: '60',
};
const THRESHOLD_SOURCES = ['ADMOB', 'ADX'];

/** Default threshold for a new account, or null when the network pays every month. */
export function defaultPayoutThreshold(sourceCode: string, currencyCode: string): string | null {
  if (!THRESHOLD_SOURCES.includes(sourceCode.toUpperCase())) return null;
  return PAYOUT_THRESHOLD_DEFAULTS[currencyCode.toUpperCase()] ?? null;
}
